import React, { useState, useEffect } from 'react';
import './App.css';
import Navigation, { Page } from './components/Navigation';
import HomePage from './components/HomePage';
import MapPage from './components/MapPage';
import Gallery from './components/Gallery';
import BookingPage from './components/BookingPage';
import ApproveBooking from './components/ApproveBooking';
import BookingManagement from './components/BookingManagement';
import {
  PaymentPage,
  PaymentSuccessPage,
  PaymentCancelledPage,
  ConfirmationPdfPage,
} from './components/GuestPaymentPages';
import AboutUs from './components/AboutUs';
import AbrolhosInfo from './components/AbrolhosInfo';
import TermsConditions from './components/TermsConditions';
import ConfirmationGenerator from './components/ConfirmationGenerator';

function App(): JSX.Element {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [selectedCampId, setSelectedCampId] = useState<string | undefined>();
  const [showApprovalPage, setShowApprovalPage] = useState(false);
  const [showManagementPage, setShowManagementPage] = useState(false);
  const [showPaymentLanding, setShowPaymentLanding] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showPaymentCancelled, setShowPaymentCancelled] = useState(false);
  const [showConfirmationPdf, setShowConfirmationPdf] = useState(false);
  const [showConfirmationGenerator, setShowConfirmationGenerator] = useState(false);

  useEffect(() => {
    const syncRoute = () => {
      const pathname = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const approval = pathname.includes('/approve-booking') || !!urlParams.get('data');
      setShowApprovalPage(approval);

      const management = pathname.includes('/manage-bookings');
      setShowManagementPage(management);
      if (management) {
        setCurrentPage('manage' as Page);
      }

      setShowPaymentLanding(pathname === '/payment' || pathname === '/payment/');
      setShowPaymentSuccess(pathname.startsWith('/payment/success'));
      setShowPaymentCancelled(pathname.startsWith('/payment/cancelled'));
      setShowConfirmationGenerator(pathname.startsWith('/confirm-booking'));
      setShowConfirmationPdf(
        pathname.startsWith('/confirmation') && !pathname.startsWith('/confirm-booking')
      );
    };

    syncRoute();

    window.addEventListener('popstate', syncRoute);
    window.addEventListener('hashchange', syncRoute);

    return () => {
      window.removeEventListener('popstate', syncRoute);
      window.removeEventListener('hashchange', syncRoute);
    };
  }, []);

  const handleNavigate = (page: Page, campId?: string) => {
    setCurrentPage(page);
    setSelectedCampId(campId);
    setShowApprovalPage(false);
    setShowManagementPage(false);
    setShowPaymentLanding(false);
    setShowPaymentSuccess(false);
    setShowPaymentCancelled(false);
    setShowConfirmationPdf(false);
    setShowConfirmationGenerator(false);
  };

  const renderPage = () => {
    if (showApprovalPage) {
      return <ApproveBooking />;
    }

    if (showManagementPage) {
      return <BookingManagement />;
    }

    if (showConfirmationGenerator) {
      return <ConfirmationGenerator />;
    }

    if (showPaymentSuccess) {
      return <PaymentSuccessPage />;
    }
    if (showPaymentCancelled) {
      return <PaymentCancelledPage />;
    }
    if (showPaymentLanding) {
      return <PaymentPage />;
    }
    if (showConfirmationPdf) {
      return <ConfirmationPdfPage />;
    }

    switch (currentPage) {
      case 'home':
        return <HomePage onNavigate={handleNavigate} />;
      case 'map':
        return <MapPage />;
      case 'info':
        return <AbrolhosInfo />;
      case 'booking':
        return <BookingPage preSelectedCampId={selectedCampId} />;
      case 'gallery':
        return <Gallery onNavigate={handleNavigate} />;
      case 'about':
        return <AboutUs />;
      case 'terms':
        return <TermsConditions />;
      default:
        return <HomePage onNavigate={handleNavigate} />;
    }
  };

  const hideMainNav =
    showApprovalPage ||
    showManagementPage ||
    showPaymentLanding ||
    showPaymentSuccess ||
    showPaymentCancelled ||
    showConfirmationPdf ||
    showConfirmationGenerator;

  return (
    <div className="App">
      {!hideMainNav && (
        <Navigation currentPage={currentPage} onPageChange={setCurrentPage} />
      )}
      <main className="app-main">
        {renderPage()}
      </main>
    </div>
  );
}

export default App;

